/**
 * Tokenização de cartão via Pagar.me v5 (client-side, PCI-safe).
 *
 * O backend v1 exige `card_token` e rejeita dados crus de cartão. Aqui o cartão
 * é tokenizado direto contra a Pagar.me usando a chave PÚBLICA (`appId`), de
 * modo que o PAN/CVV nunca trafegam pelo nosso backend nem são persistidos.
 *
 * REGRAS PCI:
 * - Nunca logar `number` (PAN) nem `cvv`.
 * - Nunca enviar dados crus do cartão para a nossa API — apenas o token (`token_...`).
 */

const PAGARME_TOKENS_URL = 'https://api.pagar.me/core/v5/tokens'

export interface ICardData {
  /** Número do cartão (com ou sem espaços) */
  number: string
  /** Nome impresso no cartão */
  holder_name: string
  /** Mês de validade (1-12) */
  exp_month: number | string
  /** Ano de validade (2 ou 4 dígitos) */
  exp_year: number | string
  /** Código de segurança */
  cvv: string
}

/** Erro de tokenização com mensagem amigável (PCI-safe, sem dados do cartão). */
export class PagarmeTokenizationError extends Error {
  public readonly status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'PagarmeTokenizationError'
    this.status = status
  }
}

const FRIENDLY_FIELD_MESSAGES: Record<string, string> = {
  'card.number': 'Número do cartão inválido.',
  'card.holder_name': 'Nome impresso no cartão inválido.',
  'card.exp_month': 'Mês de validade inválido.',
  'card.exp_year': 'Ano de validade inválido.',
  'card.cvv': 'Código de segurança (CVV) inválido.'
}

const GENERIC_ERROR = 'Não foi possível validar os dados do cartão. Confira as informações e tente novamente.'

/** Lê e valida a chave pública da Pagar.me exposta pelo Vite. */
function getPublicKey(): string {
  const key = import.meta.env.VITE_PUBLIC_PAGARME_API_KEY

  if (typeof key !== 'string' || key.trim() === '') {
    throw new PagarmeTokenizationError(
      'Configuração de pagamento ausente: defina VITE_PUBLIC_PAGARME_API_KEY no ambiente.'
    )
  }

  return key.trim()
}

/** Normaliza os campos do cartão sem alterar/expor o conteúdo sensível. */
function normalizeCard(card: ICardData) {
  return {
    number: String(card.number ?? '').replace(/\D/g, ''),
    holder_name: String(card.holder_name ?? '').trim(),
    exp_month: String(card.exp_month ?? '')
      .replace(/\D/g, '')
      .padStart(2, '0'),
    exp_year: String(card.exp_year ?? '').replace(/\D/g, ''),
    cvv: String(card.cvv ?? '').replace(/\D/g, '')
  }
}

/**
 * Constrói uma mensagem amigável a partir do payload de erro da Pagar.me,
 * sem nunca incluir dados do cartão.
 */
function buildFriendlyError(payload: unknown, status: number): PagarmeTokenizationError {
  const errors = (payload as { errors?: unknown })?.errors

  if (errors && typeof errors === 'object') {
    const friendly = Object.keys(errors as Record<string, unknown>)
      .map((field) => FRIENDLY_FIELD_MESSAGES[field])
      .filter(Boolean)

    if (friendly.length > 0) {
      return new PagarmeTokenizationError(Array.from(new Set(friendly)).join(' '), status)
    }
  }

  return new PagarmeTokenizationError(GENERIC_ERROR, status)
}

/**
 * Tokeniza um cartão na Pagar.me v5 e retorna o `card_token` (`token_...`).
 *
 * @throws {PagarmeTokenizationError} env ausente, falha de rede, recusa de
 * validação ou resposta inesperada. A mensagem é segura para exibir ao usuário
 * e nunca contém dados do cartão.
 */
export async function tokenizeCard(card: ICardData): Promise<string> {
  const appId = getPublicKey()
  const normalized = normalizeCard(card)

  let response: Response
  try {
    response = await fetch(`${PAGARME_TOKENS_URL}?appId=${encodeURIComponent(appId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ type: 'card', card: normalized })
    })
  } catch {
    // Não repassamos o erro original para evitar vazar dados em logs/Sentry.
    throw new PagarmeTokenizationError(
      'Falha de conexão ao validar o cartão. Verifique sua internet e tente novamente.'
    )
  }

  let payload: unknown = null
  try {
    payload = await response.json()
  } catch {
    payload = null
  }

  if (!response.ok) {
    throw buildFriendlyError(payload, response.status)
  }

  const token = (payload as { id?: string })?.id

  return token ?? ''
}
